import { motion, AnimatePresence } from 'motion/react'
import { cn } from '@/shared/lib/utils'
import { useTelegram } from '@/shared/lib/telegram'

export interface ConfirmDialogProps {
  isOpen: boolean
  onClose: () => void
  onConfirm: () => void
  title: string
  description?: string
  confirmText?: string
  cancelText?: string
  variant?: 'default' | 'danger'
  isLoading?: boolean
}

export function ConfirmDialog({
  isOpen,
  onClose,
  onConfirm,
  title,
  description,
  confirmText = 'Подтвердить',
  cancelText = 'Отмена',
  variant = 'default',
  isLoading = false,
}: ConfirmDialogProps) {
  const { hapticFeedback } = useTelegram()

  const handleConfirm = () => {
    hapticFeedback.impactOccurred('medium')
    onConfirm()
  }

  const handleCancel = () => {
    hapticFeedback.impactOccurred('light')
    onClose()
  }

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          {/* Backdrop + Container для центрирования */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="fixed inset-0 z-50 bg-black/50 backdrop-blur-sm flex items-center justify-center p-4"
            onClick={handleCancel}
          >
            {/* Dialog */}
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.95 }}
              transition={{ type: 'spring', stiffness: 400, damping: 30 }}
              onClick={(e) => e.stopPropagation()}
              className={cn(
                'w-full max-w-sm',
                'bg-white dark:bg-dark-card rounded-2xl shadow-xl',
                'overflow-hidden'
              )}
            >
            {/* Content */}
            <div className="p-6 text-center">
              <h2 className="text-lg font-semibold text-ink dark:text-white mb-2">
                {title}
              </h2>
              {description && (
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  {description}
                </p>
              )}
            </div>

            {/* Actions */}
            <div className="flex border-t border-silk dark:border-dark-border">
              <button
                type="button"
                onClick={handleCancel}
                disabled={isLoading}
                className={cn(
                  'flex-1 py-3.5 text-base font-medium',
                  'text-ink dark:text-white',
                  'border-r border-silk dark:border-dark-border',
                  'active:bg-gray-100 dark:active:bg-dark-elevated',
                  'transition-colors',
                  isLoading && 'opacity-50 cursor-not-allowed'
                )}
              >
                {cancelText}
              </button>
              <button
                type="button"
                onClick={handleConfirm}
                disabled={isLoading}
                className={cn(
                  'flex-1 py-3.5 text-base font-semibold',
                  'active:opacity-70',
                  'transition-colors',
                  variant === 'danger'
                    ? 'text-error'
                    : 'text-primary dark:text-primary-light',
                  isLoading && 'opacity-50 cursor-not-allowed'
                )}
              >
                {isLoading ? (
                  <span className="flex items-center justify-center gap-2">
                    <span className="w-4 h-4 border-2 border-current border-t-transparent rounded-full animate-spin" />
                    Удаление...
                  </span>
                ) : (
                  confirmText
                )}
              </button>
            </div>
            </motion.div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  )
}






